import { AuthProvider } from '@descope/react-sdk';
import * as Sentry from '@sentry/react';
import flagsmith from '@flagsmith/flagsmith';
import { FlagsmithProvider } from '@flagsmith/flagsmith/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import AuthenticatedTelemetry from './components/AuthenticatedTelemetry';
import { TimestampFormatProvider } from './contexts/TimestampFormatProvider';
import { TokenManagerProvider } from './contexts/TokenManagerProvider';
import './index.css';
import { initMixpanel } from './lib/analytics';
import { initCustomerIO } from './lib/customerio';
import { getEnvironmentName, isTelemetryEnabled } from './lib/env';
import { initializeGTM } from './lib/gtm';
import { QueryProvider } from './providers/QueryProvider';
import { ScopeChangeProvider } from './providers/ScopeChangeProvider/ScopeChangeProvider.tsx';
import { ThemeProvider } from './providers/ThemeProvider';

const envName = getEnvironmentName();
const isSentryEnabled = isTelemetryEnabled();

// Initialize Google Tag Manager
const gtmId = import.meta.env.VITE_GTM_ID;
const gtmEnabled = import.meta.env.VITE_GTM_ENABLED === 'true';
if (gtmId && gtmEnabled && isTelemetryEnabled()) {
  initializeGTM(gtmId);
}

// Initialize Mixpanel
try {
  initMixpanel();
} catch (e) {
  console.warn('Failed to initialize Mixpanel:', e);
}

// Initialize Customer.io (async: resolves CDN settings before loading the SDK)
void initCustomerIO().catch(e => {
  console.warn('Failed to initialize Customer.io:', e);
});

try {
  Sentry.init({
    enabled: isSentryEnabled,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: envName,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['warn', 'error'] }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    release: import.meta.env.VITE_APP_VERSION,
    sendDefaultPii: false,
    beforeSend(event) {
      // Filter out noisy Descope SDK console warnings/errors (captured via captureConsoleIntegration)
      const message =
        event.message || event.exception?.values?.[0]?.value || '';
      if (
        message.includes('[Descope]') ||
        message.includes('missing base element for component descope-') ||
        message.includes('Token refreshed successfully')
      ) {
        return null;
      }
      return event;
    },
    tunnel: import.meta.env.DEV
      ? '/monitoring'
      : import.meta.env.VITE_API_BASE
        ? `${import.meta.env.VITE_API_BASE}/monitoring`
        : undefined,
  });
} catch (e) {
  console.warn('Failed to initialize Sentry:', e);
}

Sentry.setTag('deployment', envName);

const flagsmithEnvKey = import.meta.env.VITE_FLAGSMITH_ENV_ID;

// Descope project ID is injected at build time via vite.config.ts
// This ensures only ONE project ID is exposed per build (not both prod + staging)
const descopeProjectId = import.meta.env.VITE_DESCOPE_PROJECT_ID;

// Fail-fast if somehow the build-time injection didn't work
if (!descopeProjectId) {
  throw new Error(
    'VITE_DESCOPE_PROJECT_ID not injected during build. Check vite.config.ts'
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}
createRoot(rootElement).render(
  <StrictMode>
    <FlagsmithProvider
      flagsmith={flagsmith}
      options={{
        environmentID: flagsmithEnvKey,
      }}
    >
      <AuthProvider projectId={descopeProjectId}>
        <QueryProvider>
          <ThemeProvider>
            <TimestampFormatProvider>
              <ScopeChangeProvider>
                <BrowserRouter>
                  <TokenManagerProvider>
                    {isSentryEnabled ? (
                      <>
                        <AuthenticatedTelemetry />
                        <Sentry.ErrorBoundary
                          fallback={<p>Something went wrong.</p>}
                        >
                          <App />
                        </Sentry.ErrorBoundary>
                      </>
                    ) : (
                      <App />
                    )}
                  </TokenManagerProvider>
                </BrowserRouter>
              </ScopeChangeProvider>
            </TimestampFormatProvider>
          </ThemeProvider>
        </QueryProvider>
      </AuthProvider>
    </FlagsmithProvider>
  </StrictMode>
);
